import React, { useState, useEffect } from 'react';
import tw from 'tailwind-styled-components';
import { FaTimes, FaTrash, FaPlus, FaMinus, FaShoppingBag, FaEllipsisV, FaCreditCard, FaMobileAlt, FaMoneyBillWave, FaChevronDown, FaChevronRight, FaInfoCircle } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import useCart from '../hooks/useCart';
import useCartActions from '../hooks/useCartAction';
import useOrderActions from '../hooks/useOrderActions';
import { Link, useNavigate } from 'react-router-dom';
import { ErrorText } from './Styles/PageStyles';
import { SmallBtn } from './Styles/InputStyles';
import { useQuery } from "react-query";
import axios from "axios";
import Config from "../Config";
import { toast } from 'react-toastify';

function Cart({ isOpen, onClose }) {
  const navigate = useNavigate();
  const user = useSelector((state) => state.UserReducer.user);
  const { data: cartData, isLoading, error } = useCart();      // Custom hook to fetch cart data
  const { addItem, updateItem, removeItem } = useCartActions();    // Custom hook for cart actions like add, update, remove
  const { placeOrder, createPaymentLink, getShippingAddressId, isPlacingOrder, isCreatingPaymentLink } = useOrderActions(); // Custom hook for order actions
  const [isNavigating, setIsNavigating] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);  // Track which dropdown is open
  const [paymentMethod, setPaymentMethod] = useState('cod');    // Payment method state
  const [notes, setNotes] = useState('');                      // Order notes state
  const [orderError, setOrderError] = useState('');
  const [isOrderInfoOpen, setIsOrderInfoOpen] = useState(false);
  const [stockErrorId, setStockErrorId] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Prevent body scroll when cart is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest('[data-dropdown]')) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  // Fetch address data
  const { data: addressesData } = useQuery(
    ['addresses', user?.token],
    async () => {
      if (!user?.token) return [];
      const response = await axios.get(`${Config.apiUrl}/addresses`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      // Handle various response structures
      const data = response.data.data || response.data || {};
      return data.addresses || data || [];
    },
    {
      enabled: !!user?.token && isOpen, // Only fetch when cart is open
      staleTime: 1000 * 60 * 5, // Cache for 5 mins
    }
  );

  // Derive default address from fetched data
  const defaultAddress = addressesData && Array.isArray(addressesData)
    ? (addressesData.find(addr => addr.is_default) || addressesData[0])
    : null;

  if (!isOpen || !user) {
    return null;
  }

  const cartItems = cartData?.items || [];

  const totalPrice = cartItems.reduce((total, item) => {
    const price = item.product?.discount_price || item.product?.price || 0;
    return total + (parseFloat(price).toFixed(2) * item.quantity);
  }, 0);                  // Calculate total price considering discounts

  // Calculate original total price without discounts
  const originalTotalPrice = cartItems.reduce((total, item) => {
    const originalPrice = item.product?.price || item.product?.discount_price || 0;
    return total + (parseFloat(originalPrice).toFixed(2) * item.quantity);
  }, 0);
  const totalItems = cartItems.reduce((total, item) => total + item.quantity, 0);  // Total number of items in cart

  // 18% tax on the subtotal
  // const taxAmount = parseFloat(totalPrice.toFixed(2)) * 0.18;

  // new total including tax and shipping charges

  // const discountedTotal = parseFloat(totalPrice.toFixed(2)) + parseFloat(taxAmount.toFixed(2)) + 50; // shipping charges of 50 rupees (static for now)
  // const newTotal = parseFloat(originalTotalPrice.toFixed(2)) + parseFloat(taxAmount.toFixed(2)) + 50; // original total including tax and shipping charges

  const discountedTotal = parseFloat(totalPrice.toFixed(2)) + 50; // shipping charges of 50 rupees (static for now)
  const newTotal = parseFloat(originalTotalPrice.toFixed(2)) + 50; // original total including shipping charges

  const handleIncrease = (item) => {
    if (item.quantity >= item.product.stock_quantity) {
      setStockErrorId(item.id);
      return;
    }
    setStockErrorId(null);
    updateItem.mutate({ id: item.id, quantity: item.quantity + 1 });  // Increase item quantity
  };

  const handleDecrease = (item) => {                    // Decrease item quantity
    setStockErrorId(null);
    if (item.quantity > 1) {
      updateItem.mutate({ id: item.id, quantity: item.quantity - 1 });
    } else {
      removeItem.mutate(item.id);
    }
  };

  const handleStartShopping = (e) => {
    e.preventDefault();
    setIsNavigating(true);

    setTimeout(() => {
      onClose();
      // Navigate to home 
      setTimeout(() => {
        window.location.href = '/';
      }, 200);
    }, 600);
  };

  const toggleDropdown = (itemId) => {
    setOpenDropdownId(openDropdownId === itemId ? null : itemId);
  };

  const handleDeleteItem = (itemId) => {
    removeItem.mutate(itemId);
    setOpenDropdownId(null); // Close dropdown after delete
  };


  const handlePlaceOrder = async () => {
    try {

      if (!cartItems || cartItems.length === 0) {
        // console.log('Cart is empty, cannot place order');
        return; // Don't proceed if cart is empty
      }

      if (!defaultAddress) {
        setOrderError('Please add a delivery address to place an order.');
        setTimeout(() => setOrderError(''), 4000);
        return; // Don't proceed if no address is available
      }

      setIsProcessing(true);

      const shippingAddressId = defaultAddress.id; // default address ID for shipping

      // console.log('Placing order with:', {
      //   shippingAddressId,
      //   itemCount: cartItems.length,
      //   hasDefaultAddress: !!defaultAddress
      // });

      // Prepare items payload from cart
      const items = cartItems.map(item => ({
        product_id: item.product?.id,
        quantity: item.quantity
      }));

      const payload = {
        shipping_address_id: shippingAddressId,
        payment_method: paymentMethod,
        notes: notes || '',
        items: items
      };

      // Use the mutation from the hook
      const orderResponse = await placeOrder.mutateAsync(payload); //create order

      // extract order ID from the response
      const responseData = orderResponse.data || orderResponse;
      const orderId = responseData.order?.id || responseData.id || responseData.orderId;

      if (!orderId) {
        setOrderError("Order placed but ID missing. Cannot initiate payment.");
        setIsProcessing(false); // Reset processing state on error
        return;
      }

      if (paymentMethod === 'cod') {
        setIsProcessing(false);
        setNotes('');
        onClose();
        navigate('/orders');
        return;
      }

      // Create Payment Link
      const paymentPayload = { orderId: orderId };
      const paymentResponse = await createPaymentLink.mutateAsync(paymentPayload);

      // extract payment link URL
      const paymentData = paymentResponse.data || paymentResponse;
      const linkUrl = paymentData.link_url || (paymentData.data && paymentData.data.link_url);

      // Redirect to Cashfree payment link
      if (linkUrl) {
        window.location.href = linkUrl;
      } else {
        console.error("Payment Link Data:", paymentData);
        setOrderError("Failed to generate payment link.");
        setIsProcessing(false); // Reset processing state on error
      }

      // Reset form and close cart on success
      setNotes('');
      setPaymentMethod('upi');
      // onClose();

    } catch (error) {
      console.error('Order placement error:', error);
      setIsProcessing(false); // Reset processing state on error
    }
  };

  const handleAddAddressClick = () => {
    onClose(); // Close the cart modal
    navigate('/profile', { state: { openAddressModal: true } }); // Navigate to profile
  };

  const handleProductClick = (item) => {
    if (item && item.product && item.product.id) {
      onClose(); // Close the cart modal
      navigate(`/product/${item.product.id}`, { state: { productData: item.product } });
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()} className={isNavigating ? 'slide-out' : ''}>
        <Header>
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>
          <Title>Order Details</Title>
        </Header>

        <ScrollableContent>
          {isProcessing ? (
            <LoadingContainer>
              <LoadingSpinnerCart />
              <LoadingTextCart>
                {isCreatingPaymentLink ? 'Initiating Payment...' : 'Placing Order...'}
              </LoadingTextCart>
            </LoadingContainer>
          ) : (
            <>
              {/* Loading spinner while fetching cart data */}
              {isLoading && (
                <LoadingContainer>
                  <LoadingSpinnerCart />
                  <LoadingTextCart>Loading your cart...</LoadingTextCart>
                </LoadingContainer>
              )}

              {/* Display error if cart fails to load */}
              {error && (
                <ErrorText>Could not load cart. Please try again.</ErrorText>
              )}

              {/* Display empty cart message if there is no item in the cart */}
              {cartItems.length === 0 && !isLoading && !error && (
                <EmptyCart>
                  <EmptyBagIcon>
                    <FaShoppingBag />
                  </EmptyBagIcon>
                  <EmptyText>Your bag is empty</EmptyText>
                  <EmptySubtext>Add items to your bag to see them here.</EmptySubtext>
                  <StartShoppingButton onClick={handleStartShopping} disabled={isNavigating}>
                    {isNavigating ? (
                      <>
                        <LoadingSpinner />
                        <LoadingText>Loading...</LoadingText>
                      </>
                    ) : (
                      'Start Shopping'
                    )}
                  </StartShoppingButton>
                </EmptyCart>
              )}

              {/* Delivery Location Section */}
              {totalItems > 0 && (
                <DeliverySection>
                  {defaultAddress ? (
                    <>
                      <div className='flex flex-row justify-between items-center my-1'>
                        <SectionTitle>Delivery Location</SectionTitle>
                        <AddAddress onClick={handleAddAddressClick}>
                          Change
                        </AddAddress>
                      </div>
                      <AddressInfo>
                        <AddressText>
                          {defaultAddress.address_line_1}
                          {defaultAddress.address_line_2 && `, ${defaultAddress.address_line_2}`}
                          <br />
                          {defaultAddress.city}, {defaultAddress.state} - {defaultAddress.postal_code}
                          {defaultAddress.country && <br />}{defaultAddress.country}
                        </AddressText>
                      </AddressInfo>
                    </>
                  ) : (
                    <div className='flex flex-row justify-between items-center my-1'>
                      <SectionTitle>Delivery Location</SectionTitle>
                      <AddAddress onClick={handleAddAddressClick}>
                        <FaPlus className="w-3 h-3 mr-1" />
                        Add Address
                      </AddAddress>
                    </div>
                  )}

                  {orderError && (
                    <ErrorMessage>{orderError}</ErrorMessage>
                  )}
                </DeliverySection>
              )}


              {totalItems > 0 && (
                <MyCart>
                  <SectionTitle>My Cart {totalItems > 0 && <span className="ml-1">({totalItems})</span>}</SectionTitle>
                </MyCart>
              )}

              {!isLoading && !error && cartItems.length > 0 && (
                <Content>
                  {cartItems.map((item) => (
                    <CartItem key={item.id}>
                      <ItemImage onClick={() => handleProductClick(item)}
                        className="cursor-pointer" src={item.product?.images?.[0]?.image_url} alt={item.product?.name} />
                      <ItemDetails>
                        <ItemHeader onClick={() => handleProductClick(item)} className="cursor-pointer hover:kuality-blue">
                          <ItemName>{item.product?.name}</ItemName>
                        </ItemHeader>
                        <QuantityControls> {/* Quantity buttons */}
                          <QtyButton onClick={() => handleDecrease(item)} disabled={updateItem.isLoading}>
                            <FaMinus />
                          </QtyButton>
                          <QtyValue>{item.quantity}</QtyValue>
                          <QtyButton onClick={() => handleIncrease(item)} disabled={updateItem.isLoading}>
                            <FaPlus />
                          </QtyButton>
                        </QuantityControls>
                        <PriceSection>
                          {item.product?.price && item.product?.discount_price && parseFloat(item.product.price) > parseFloat(item.product.discount_price).toFixed(2) ? (
                            <PriceContainer>
                              <OriginalPrice>Rs. {parseFloat(item.product.price).toFixed(2)}</OriginalPrice>
                              <CurrentPrice>Rs. {parseFloat(item.product.discount_price).toFixed(2)}</CurrentPrice>
                            </PriceContainer>
                          ) : (
                            <CurrentPrice>Rs. {parseFloat(item.product?.price || item.product?.discount_price || 0).toFixed(2)}</CurrentPrice>
                          )}
                        </PriceSection>

                      </ItemDetails>
                      {stockErrorId === item.id && (
                        <ItemStockError>
                          Only {item.product.stock_quantity} {item.product.stock_quantity === 1 ? 'piece' : 'pieces'} left.
                        </ItemStockError>
                      )}
                    </CartItem>
                  ))}
                </Content>
              )}

              {/* Payment Method Section */}
              {totalItems > 0 && (
                <PaymentSection>
                  <SectionTitle>Payment Method</SectionTitle>
                  <PaymentOptions>
                    <PaymentButton
                      // $active={paymentMethod === 'upi'}
                      // $active={false} 
                      // onClick={() => setPaymentMethod('upi')}
                      $disabled={true}
                      onClick={() => toast.info('Only Cash on Delivery is available for now.')}
                    >
                      <FaMobileAlt />
                      <span>UPI</span>
                    </PaymentButton>
                    <PaymentButton
                      // $active={paymentMethod === 'card'}
                      // onClick={() => setPaymentMethod('card')}
                      $disabled={true}
                      onClick={() => toast.info('Only Cash on Delivery is available for now.')}
                    >
                      <FaCreditCard />
                      <span>Card</span>
                    </PaymentButton>
                    <PaymentButton
                      $active={paymentMethod === 'cod'}
                      onClick={() => setPaymentMethod('cod')}
                    >
                      <FaMoneyBillWave />
                      <span>COD</span>
                    </PaymentButton>
                  </PaymentOptions>
                </PaymentSection>
              )}

              {/* Order Notes Section */}
              {totalItems > 0 && (
                <NotesSection>
                  <SectionTitle>Notes</SectionTitle>
                  <NotesInput
                    placeholder="Delivery instructions for your order..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    maxLength={200}
                  />
                </NotesSection>
              )}
            </>
          )}

        </ScrollableContent>

        {totalItems > 0 && !isProcessing && (
          <Footer>
            <OrderInfoSection>
              <button
                className="flex justify-between items-center w-full"
                onClick={() => setIsOrderInfoOpen(!isOrderInfoOpen)}
              >
                <SectionTitle>Order Info</SectionTitle>
                <span className="text-gray-600">
                  {isOrderInfoOpen ? <FaChevronDown /> : <FaInfoCircle />}
                </span>
              </button>
              {isOrderInfoOpen && (
                <div className="mt-2">
                  <OrderInfoRow>
                    <span>Subtotal</span>
                    <div className="text-right">
                      {originalTotalPrice > totalPrice ? (
                        <SubtotalPriceContainer>
                          <div className="text-xs text-gray-400 line-through">Rs. {parseFloat(originalTotalPrice).toFixed(2)}</div>
                          <div className="font-semibold">Rs. {parseFloat(totalPrice).toFixed(2)}</div>
                        </SubtotalPriceContainer>
                      ) : (
                        <span>Rs. {parseFloat(totalPrice).toFixed(2)}</span>
                      )}
                    </div>
                  </OrderInfoRow>

                  {/* {cartData?.delivery_fee && ( */}
                  <OrderInfoRow>
                    <span>Shipping</span>
                    <span>Rs. 50</span>
                  </OrderInfoRow>
                  {/* )} */}

                  {/* {cartData?.tax_amount && ( */}
                  {/* <OrderInfoRow>
                    <span>Tax (18%)</span>
                    <span>Rs. {parseFloat(taxAmount).toFixed(2)}</span>
                  </OrderInfoRow> */}
                  {/* )} */}

                  <OrderInfoDivider />

                  <OrderInfoTotal>
                    <span className="font-bold">Total</span>
                    {/* <span className="font-bold">Rs. {cartData?.total_amount ? parseFloat(cartData.total_amount).toFixed(2) : totalPrice.toFixed(2)}</span> */}
                    <span className="font-bold">Rs. {parseFloat(discountedTotal).toFixed(2)}</span>
                  </OrderInfoTotal>
                </div>
              )}
            </OrderInfoSection>
          </Footer>
        )}

        {totalItems > 0 && !isProcessing && (            // show place order button only if the bag is not empty and not processing
          <div className='flex flex-row px-2 py-0'>
            <Total>
              {newTotal > discountedTotal ? (
                <>
                  <div className="text-xs text-gray-400 line-through">Rs. {parseFloat(newTotal).toFixed(2)}</div>
                  <div className="font-semibold">Rs. {parseFloat(discountedTotal).toFixed(2)}</div>
                </>
              ) : (
                <span>Rs. {parseFloat(newTotal).toFixed(2)}</span>
              )}
            </Total>
            <CheckoutButton
              disabled={isLoading || isPlacingOrder || isCreatingPaymentLink}
              onClick={handlePlaceOrder}
            >
              {isPlacingOrder || isCreatingPaymentLink ? (
                <>
                  <div className='flex flex-row gap-3'>
                    <LoadingSpinner className='ml-8 md:ml-16' />
                    {/* <span>{isCreatingPaymentLink ? 'Initiating Payment...' : 'Placing Order...'}</span> */}
                    <span>Placing Order...</span>
                  </div>
                </>
              ) : (
                'Place Order'
              )}
            </CheckoutButton>
          </div>
        )}

      </ModalContainer>
    </ModalOverlay>
  );
}

const ModalOverlay = tw.div`
  fixed inset-0 bg-black bg-opacity-50 flex justify-end z-50 transition-all duration-500
`;

const ModalContainer = tw.div`
  bg-white w-full sm:w-96 lg:w-1/3 h-full shadow-2xl flex flex-col rounded-l-lg transition-all duration-500 transform translate-x-0

  &.slide-out {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const Header = tw.div`
  p-2 flex justify-center items-center relative text-center
`;

const Title = tw.h2`
  text-xl font-semibold text-gray-800 
`;

const CloseButton = tw.button`
  text-gray-400 hover:text-gray-600 transition-colors absolute top-4 right-4 z-10
`;

const MyCart = tw.div`
  px-4 py-1 
`;

const SectionTitle = tw.h3`
  text-md font-semibold text-gray-800 mb-1
`;

const DeliverySection = tw.div`
  px-4 py-1 border-b border-gray-100
`;

const AddAddress = tw.button`
  bg-kuality-blue hover:bg-kuality-dark text-white px-2 py-1 font-medium transition-colors flex items-center text-sm rounded-md
`;

const AddressInfo = tw.div`
  flex items-center gap-2
`;

const LocationIcon = tw.span`
  text-lg
`;

const AddressText = tw.span`
  text-gray-600 text-sm
`;

const PaymentSection = tw.div`
   px-4 py-2 border-y border-gray-100
`;

const PaymentOptions = tw.div`
  flex flex-row gap-2 justify-between w-full
`;

const PaymentButton = tw.button`
  flex-1 flex flex-col items-center justify-center py-1 px-1 border rounded-lg transition-all duration-200
  ${(props) => props.$disabled 
    ? 'bg-gray-50 border-gray-300 text-gray-600 cursor-not-allowed opacity-60' 
    : props.$active
      ? 'border-kuality-dark bg-kuality-dark text-white'
      : 'border-gray-300 bg-white text-gray-600 hover:border-kuality-dark'
  }
  text-xs font-medium gap-1
`;

const NotesSection = tw.div`
items-center gap-4 px-4 py-1 border-b border-gray-100
`;

const NotesInput = tw.textarea`
  w-full p-2 border border-gray-300 rounded-lg text-sm resize-none
  focus:outline-none placeholder-gray-400 h-16
`;

const OrderInfoSection = tw.div`
  px-4 py-1 bg-gray-50
`;

const OrderInfoRow = tw.div`
  flex justify-between items-center py-1 text-sm text-gray-600
`;

const OrderInfoDivider = tw.div`
  border-t border-gray-200 my-1
`;

const OrderInfoTotal = tw.div`
  flex justify-between items-center py-1 text-base text-gray-800 font-semibold
`;

const ScrollableContent = tw.div`
  flex-1 overflow-y-auto
`;

const Content = tw.div`
`;

const CartItem = tw.div`
  flex flex-row px-4 py-3 border-b border-gray-100 last:border-b-0 items-center gap-1 flex-wrap
`;

const DropdownContainer = tw.div`
  absolute top-7 right-4
`;

const DropdownButton = tw.button`
  text-gray-400 hover:text-gray-600 transition-all duration-200 ease-in-out hover:scale-105 p-1 rounded
`;

const DropdownMenu = tw.div`
  absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]
`;

const DropdownItem = tw.button`
  w-full px-6 py-2 text-center text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 
  transition-colors duration-200 rounded-lg flex items-center gap-2
  first:rounded-t-lg last:rounded-b-lg
`;

const ItemImage = tw.img`
  w-16 h-16 object-cover rounded-lg float-left mr-3
`;

const ItemDetails = tw.div`
  flex flex-1 flex-row justify-between items-center overflow-hidden
`;

const ItemHeader = tw.div`
  mb-0.5
`;

const ItemName = tw.h3`
  font-semibold text-gray-800 text-sm mb-0.5 line-clamp-2 flex-shrink whitespace-normal text-left flex-grow-1 w-20
`;

const PriceContainer = tw.div`
  flex flex-col items-end
`;

const PriceSection = tw.div`
  mb-0.5 text-right w-1/4
`;

const CurrentPrice = tw.div`
  font-bold text-gray-800 text-sm
`;

const OriginalPrice = tw.div`
  text-xs text-gray-400 line-through
`;

const SubtotalPriceContainer = tw.div`
  text-right
`;

const QuantityControls = tw.div`
  flex items-center justify-between w-14 h-6
  bg-kuality-blue text-white border font-bold 
  shadow-md hover:shadow-lg transition-shadow duration-200
  flex-shrink-0 rounded-md
`;

const QtyButton = tw.button`
  w-2 h-2 h-full flex items-center justify-center 
  hover:bg-kuality-dark
  transition-colors duration-200 mx-1
  disabled:opacity-50 
`;

const QtyValue = tw.span`
  w-full h-full flex items-center justify-center text-sm disabled:opacity-50 
`;

const Footer = tw.div`
  border-t border-gray-100 bg-white
  pb-0 md:pb-0
`;

const SubtotalSection = tw.div`
  p-4 border-b border-gray-100
`;

const SubtotalRow = tw.div`
  flex justify-between items-center
`;

const SubtotalPrice = tw.div`
  text-right
`;

// const CheckoutButton = tw.button`
//   w-full py-4 bg-black text-white font-semibold hover:bg-gray-800 transition-colors 
//   disabled:opacity-50 disabled:cursor-not-allowed mx-4 mt-4
//   mb-20 md:mb-2
// `;
const CheckoutButton = tw.button`w-1/2 max-w-s mx-auto py-1.5 bg-kuality-blue text-white font-semibold hover:bg-kuality-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed my-2 focus:outline-none rounded-md`;

const Total = tw.div`w-1/2 max-w-s mx-auto py-2 px-4 font-semibold text-gray-800 text-md flex flex-col`;

const EmptyCart = tw.div`
  flex flex-col items-center justify-center p-8 text-center h-full
`;

const EmptyBagIcon = tw.div`
  text-6xl mb-4 opacity-60 animate-pulse
`;

const EmptyText = tw.h3`
  text-lg font-medium text-gray-800
`;

const EmptySubtext = tw.p`
  text-gray-500 mb-6 text-sm
`;

const StartShoppingButton = tw.button`
  bg-gradient-to-br from-gray-600 to-gray-500
  text-white border-none px-8 py-3 rounded-full text-base font-semibold
  cursor-pointer relative overflow-hidden
  transition-all duration-200 ease-out
  shadow-lg shadow-gray-500/20
  flex items-center justify-center min-w-[180px]
  hover:from-gray-700 hover:to-gray-600 hover:scale-105 hover:shadow-xl hover:shadow-gray-500/30
  active:scale-95 active:transition-all active:duration-150 active:ease-in-out
  focus:outline-none focus:ring-4 focus:ring-gray-500/30
  disabled:opacity-80 disabled:cursor-not-allowed disabled:from-gray-500 disabled:to-gray-600 disabled:hover:scale-100
`;

const LoadingSpinner = tw.div`
  w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin
`;

const LoadingText = tw.span`
  ml-2
`;

const LoadingContainer = tw.div`
  flex flex-col items-center justify-center p-8 text-center h-full 
`;

const LoadingSpinnerCart = tw.div`
  animate-spin rounded-full h-8 w-8 border-b-2 border-red-600
`;

const LoadingTextCart = tw.p`
  mt-2 text-gray-600
`;

const ErrorMessage = tw(ErrorText)`
  text-center text-red-700 text-sm font-medium p-1 m-0
`;

const ItemStockError = tw.p`
  w-full text-right text-red-600 text-xs font-medium
`;

export default Cart;